/* Wonder Skin Skincare Quiz – Catalog Loader v7
   Lädt über die Storefront API alle veröffentlichten Produkte mit custom.quiz_professional_profile (Inhalt: Profil v7) und custom.expiry_date,
   cached schlank in localStorage (ca. 1 MB statt 6,6 MB) und liefert Produkte in der Form, die Engine v7 erwartet.
*/
(function () {
  'use strict';
  const CACHE_SCHEMA_VERSION = 70;
  const DEFAULT_API_VERSION = '2026-07';
  const INDEX_METAFIELDS = [
    { namespace: 'custom', key: 'quiz_professional_profile' }, // bestehendes Feld, Inhalt = Profil v7
    { namespace: 'custom', key: 'expiry_date' },
    { namespace: 'reviews', key: 'rating' },
    { namespace: 'reviews', key: 'rating_count' }
  ];
  const DETAIL_METAFIELDS = [
    { namespace: 'custom', key: 'vorteile_ideal' },
    { namespace: 'custom', key: 'anwendung_produkt' }
  ];
  const PRODUCT_FIELDS = `
    id handle title vendor updatedAt availableForSale onlineStoreUrl
    featuredImage { url altText width height }
    selectedOrFirstAvailableVariant { id title availableForSale quantityAvailable price { amount currencyCode } compareAtPrice { amount currencyCode } image { url altText width height } }
    metafields(identifiers: $identifiers) { namespace key type value }`;
  const QUERY_INDEX = `#graphql
    query SkinQuizV7Products($first: Int!, $after: String, $identifiers: [HasMetafieldsIdentifier!]!) {
      products(first: $first, after: $after, sortKey: ID) { pageInfo { hasNextPage endCursor } nodes { ${PRODUCT_FIELDS} } } }`;
  const QUERY_COLLECTION = `#graphql
    query SkinQuizV7Collection($handle: String!, $first: Int!, $after: String, $identifiers: [HasMetafieldsIdentifier!]!) {
      collection(handle: $handle) { products(first: $first, after: $after) { pageInfo { hasNextPage endCursor } nodes { ${PRODUCT_FIELDS} } } } }`;
  const QUERY_DETAILS = `#graphql
    query SkinQuizV7Details($ids: [ID!]!, $identifiers: [HasMetafieldsIdentifier!]!) { nodes(ids: $ids) { ... on Product { ${PRODUCT_FIELDS} } } }`;

  class StorefrontApiError extends Error { constructor(message, details) { super(message); this.name = 'StorefrontApiError'; this.details = details || null; } }

  class StorefrontClient {
    constructor(config) {
      this.domain = String(config.domain || '').trim();
      this.token = String(config.token || '').trim();
      this.apiVersion = String(config.apiVersion || DEFAULT_API_VERSION).trim();
    }
    async request(query, variables) {
      if (!this.domain || !this.token) throw new StorefrontApiError('Storefront-Konfiguration fehlt');
      const response = await fetch(`https://${this.domain}/api/${this.apiVersion}/graphql.json`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': this.token },
        body: JSON.stringify({ query, variables })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload || payload.errors) throw new StorefrontApiError('Storefront API Fehler', payload && payload.errors);
      return payload.data;
    }
  }

  function metafieldMap(list) { const m = {}; (list || []).filter(Boolean).forEach(x => { m[`${x.namespace}.${x.key}`] = x; }); return m; }
  function richTextToPlain(value) {
    if (!value) return '';
    let doc = null; try { doc = JSON.parse(value); } catch (_) { return String(value).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(); }
    const lines = [];
    const walk = node => {
      if (!node) return;
      if (Array.isArray(node)) { node.forEach(walk); return; }
      if (node.type === 'text') { lines.push({ inline: true, text: node.value || '' }); return; }
      if (node.type === 'list-item' || node.type === 'paragraph' || node.type === 'heading') lines.push({ inline: false, text: '' });
      walk(node.children);
    };
    walk(doc);
    const out = []; let cur = '';
    lines.forEach(l => { if (l.inline) cur += l.text; else { if (cur.trim()) out.push(cur.trim()); cur = ''; } });
    if (cur.trim()) out.push(cur.trim());
    return out.join('\n');
  }
  function normalize(node) {
    const f = metafieldMap(node.metafields);
    const scalar = (ns, k) => String(((f[`${ns}.${k}`] || {}).value) || '').trim();
    let v7 = null; try { v7 = JSON.parse(scalar('custom', 'quiz_professional_profile') || 'null'); } catch (_) { v7 = null; }
    const variant = node.selectedOrFirstAvailableVariant || null;
    const qty = variant ? variant.quantityAvailable : null;
    let rating = null; try { const r = JSON.parse(scalar('reviews', 'rating') || 'null'); rating = r && r.value ? Number(r.value) : null; } catch (_) { rating = Number(scalar('reviews', 'rating')) || null; }
    return {
      id: node.id, handle: node.handle, title: node.title, vendor: node.vendor || '', url: node.onlineStoreUrl || '',
      image: (variant && variant.image) || node.featuredImage || null, variant, updatedAt: node.updatedAt,
      price: variant && variant.price ? Number(variant.price.amount) : 0,
      compareAtPrice: variant && variant.compareAtPrice ? Number(variant.compareAtPrice.amount) : null,
      currency: variant && variant.price ? variant.price.currencyCode : 'EUR',
      available: Boolean(node.availableForSale) && Boolean(variant && variant.availableForSale) && !(qty !== null && qty !== undefined && Number(qty) <= 0),
      expiry: scalar('custom', 'expiry_date') || null,
      rating, ratingCount: Number(scalar('reviews', 'rating_count')) || 0,
      v7: v7 && typeof v7 === 'object' && v7.v === 7 ? v7 : null // nur Profil v7, alte v6-Daten werden ignoriert
    };
  }

  class CatalogService {
    constructor(config) {
      this.client = new StorefrontClient(config);
      this.collectionHandle = String(config.collectionHandle || '').trim();
      this.pageSize = Math.min(250, Math.max(20, Number(config.pageSize || 100)));
      this.cacheMinutes = Math.max(1, Number(config.cacheMinutes || 15));
      this.debug = Boolean(config.debug);
      this.cacheKey = `skin-quiz-v7:${CACHE_SCHEMA_VERSION}:${this.client.apiVersion}:${this.client.domain}:${this.collectionHandle || 'all'}`;
    }
    log() { if (this.debug && window.console) console.info('[SkinQuizCatalogV7]', ...arguments); }
    readCache() {
      try { const raw = localStorage.getItem(this.cacheKey); if (!raw) return null; const p = JSON.parse(raw); return p && p.schemaVersion === CACHE_SCHEMA_VERSION && Array.isArray(p.products) ? p : null; } catch (_) { return null; }
    }
    writeCache(products) {
      const payload = { schemaVersion: CACHE_SCHEMA_VERSION, savedAt: new Date().toISOString(), products };
      try { localStorage.setItem(this.cacheKey, JSON.stringify(payload)); } catch (e) { this.log('Cache write failed', e); }
      return payload;
    }
    isFresh(cache) { if (!cache || !cache.savedAt) return false; const age = Date.now() - new Date(cache.savedAt).getTime(); return Number.isFinite(age) && age < this.cacheMinutes * 60 * 1000; }
    async load(options) {
      const onProgress = options && typeof options.onProgress === 'function' ? options.onProgress : function () {};
      const cached = this.readCache();
      if (cached && this.isFresh(cached) && cached.products.length) return { products: cached.products, source: 'cache' };
      try {
        const products = await this.fullSync(onProgress);
        this.writeCache(products);
        return { products, source: 'network' };
      } catch (e) {
        if (cached && cached.products.length) { this.log('Netzwerk fehlgeschlagen, Cache verwendet', e); return { products: cached.products, source: 'stale-cache' }; }
        throw e;
      }
    }
    async fullSync(onProgress) {
      const products = []; let after = null; let page = 0;
      for (;;) {
        const data = this.collectionHandle
          ? await this.client.request(QUERY_COLLECTION, { handle: this.collectionHandle, first: this.pageSize, after, identifiers: INDEX_METAFIELDS })
          : await this.client.request(QUERY_INDEX, { first: this.pageSize, after, identifiers: INDEX_METAFIELDS });
        const conn = this.collectionHandle ? (data.collection && data.collection.products) : data.products;
        if (!conn) break;
        (conn.nodes || []).forEach(n => { const p = normalize(n); if (p.v7 && p.v7.role && p.v7.role !== 'excluded') products.push(p); });
        page += 1; onProgress({ page, loaded: products.length });
        if (!conn.pageInfo || !conn.pageInfo.hasNextPage) break;
        after = conn.pageInfo.endCursor;
      }
      return products;
    }
    async getFreshDetails(ids) {
      const unique = Array.from(new Set((ids || []).filter(Boolean))).slice(0, 20);
      if (!unique.length) return [];
      const data = await this.client.request(QUERY_DETAILS, { ids: unique, identifiers: DETAIL_METAFIELDS });
      return (data.nodes || []).filter(Boolean).map(node => {
        const base = normalize(node); const f = metafieldMap(node.metafields);
        const scalar = (ns, k) => String(((f[`${ns}.${k}`] || {}).value) || '').trim();
        base.benefits = richTextToPlain(scalar('custom', 'vorteile_ideal')).split('\n').map(s => s.trim()).filter(s => s && !/^das wichtigste auf einen blick$/i.test(s));
        base.usage = scalar('custom', 'anwendung_produkt');
        return base;
      });
    }
    clearCache() { try { localStorage.removeItem(this.cacheKey); } catch (_) {} }
  }

  window.SkinQuizCatalogV7 = { CatalogService, StorefrontClient, StorefrontApiError, normalize, richTextToPlain, constants: { CACHE_SCHEMA_VERSION, DEFAULT_API_VERSION } };
})();
